import { invokeEdgeFunction } from "@/services/edgeApi"
import type { PanchayatDataMap } from "./panchayat.types"
import type { V2Hierarchy, V2Names, V2Ward } from "./xlsx-loader"

export const SAMPLE_PANCHAYAT_MAP: PanchayatDataMap = {}
const PERF_DEBUG_SESSION_ID = "slow-page-load"

function reportPerfDebug(hypothesisId: string, location: string, msg: string, data: Record<string, unknown>) {
  if (!(import.meta as any)?.env?.DEV) return
  if (typeof window === "undefined" || (window as any).__TRAE_PERF_DEBUG__ !== true) return
  if (typeof fetch !== "function") return
  const perfDebugUrl = String((window as any).__TRAE_LOCAL_DEBUG_URL__ || "").trim()
  if (!perfDebugUrl) return
  fetch(perfDebugUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sessionId: PERF_DEBUG_SESSION_ID,
      runId: "phase-14",
      hypothesisId,
      location,
      msg,
      data,
      ts: Date.now(),
    }),
  }).catch(() => { })
}

export type PanchayatDistrictRecord = {
  id: string
  names: V2Names
}

export type PanchayatBlockRecord = {
  id: string
  names: V2Names
  districtId: string
}

export type PanchayatGramPanchayatRecord = {
  id: string
  names: V2Names
  districtId: string
  blockId: string
}

export type PanchayatInstitutionRecord = {
  id: string
  code: string
  type: "current" | "previous"
  category: "gp"
  names: V2Names
  districtId: string
  blockId: string
  districtNames: V2Names
  blockNames: V2Names
  sarpanch: V2Names
  wards: V2Ward[]
  latitude?: number | null
  longitude?: number | null
  members?: unknown
  rawData?: Record<string, any>
}

export interface PanchayatDataProvider {
  getDistricts(): Promise<PanchayatDistrictRecord[]>
  getBlocks(districtId: string): Promise<PanchayatBlockRecord[]>
  getGramPanchayats(blockId: string, districtId?: string): Promise<PanchayatGramPanchayatRecord[]>
  getInstitution(
    gpId: string,
    districtId?: string,
    blockId?: string,
    recordType?: "current" | "previous",
  ): Promise<PanchayatInstitutionRecord | null>
}

type InstitutionNodeType = "district" | "block" | "gp"

type InstitutionNode = {
  code?: string
  id?: string
  type?: InstitutionNodeType
  parent_code?: string | null
  district_id?: string
  name?: Partial<V2Names>
  name_english?: string
  name_hindi?: string
}

type PanchayatChildrenPayload = {
  type?: InstitutionNodeType | null
  parent_code?: string | null
  children?: InstitutionNode[]
}

type DistrictBootstrapPayload = {
  districts?: Array<{
    id?: string
    names?: Partial<V2Names>
  }>
}

type PanchayatDataPayload = {
  code?: string
  type?: "current" | "previous"
  category?: "district" | "block" | "gp"
  data?: Record<string, any>
  panchayat?: {
    id?: string
    code?: string
    type?: "current" | "previous"
    category?: "gp"
    district?: string
    block?: string
    gram_panchayat?: string
    latitude?: number | null
    longitude?: number | null
    members?: unknown
    data?: Record<string, any>
  }
}

type CacheEntry<T> = {
  value: T
  expiresAt: number
}

const DISTRICT_TTL_MS = 24 * 60 * 60_000
const BLOCK_TTL_MS = 6 * 60 * 60_000
const GP_TTL_MS = 6 * 60 * 60_000
const INSTITUTION_TTL_MS = 30 * 60_000

const DATASET_VERSION_POLL_MS = 5 * 60_000

let districtListCache: CacheEntry<PanchayatDistrictRecord[]> | null = null
let districtListInFlight: Promise<PanchayatDistrictRecord[]> | null = null
const blockListCache = new Map<string, CacheEntry<PanchayatBlockRecord[]>>()
const blockListInFlightCache = new Map<string, Promise<PanchayatBlockRecord[]>>()
const gpListCache = new Map<string, CacheEntry<PanchayatGramPanchayatRecord[]>>()
const gpListInFlightCache = new Map<string, Promise<PanchayatGramPanchayatRecord[]>>()
const institutionCache = new Map<string, CacheEntry<PanchayatInstitutionRecord | null>>()
const institutionInFlightCache = new Map<string, Promise<PanchayatInstitutionRecord | null>>()
let activePanchayatDataProvider: PanchayatDataProvider | null = null
let hierarchyInFlight: Promise<V2Hierarchy> | null = null
const loadedDistrictByBlockKey = new Map<string, string>()
const loadedDistrictByGpKey = new Map<string, string>()
const loadedBlockByGpKey = new Map<string, string>()
const EMPTY_NAMES: V2Names = { en: "", hi: "", pa: "" }

type DatasetMetaPayload = {
  version?: unknown
}

let datasetVersion: string | null = null
let datasetVersionCheckedAt = 0
let datasetVersionInFlight: Promise<string | null> | null = null

function normalizeString(value: unknown) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
}

function readFreshCacheEntry<T>(entry: CacheEntry<T> | null | undefined): CacheEntry<T> | null {
  if (!entry) return null
  if (Date.now() >= entry.expiresAt) return null
  return entry
}

async function loadDatasetVersion(): Promise<string | null> {
  const now = Date.now()
  if (datasetVersion && now - datasetVersionCheckedAt < DATASET_VERSION_POLL_MS) return datasetVersion
  if (!datasetVersion && datasetVersionCheckedAt && now - datasetVersionCheckedAt < DATASET_VERSION_POLL_MS) return null
  if (datasetVersionInFlight) return datasetVersionInFlight

  const request = (async () => {
    try {
      const res = await fetch("/data/npl_sync_meta.json", { cache: "no-cache" })
      if (!res.ok) return null
      const parsed = (await res.json()) as DatasetMetaPayload
      const version = typeof parsed?.version === "string" ? parsed.version.trim() : ""
      return version || null
    } catch {
      return null
    } finally {
      datasetVersionCheckedAt = Date.now()
    }
  })()

  datasetVersionInFlight = request
  try {
    return await request
  } finally {
    if (datasetVersionInFlight === request) datasetVersionInFlight = null
  }
}

async function ensureDatasetVersion(): Promise<void> {
  const next = await loadDatasetVersion()
  if (!next) return
  if (!datasetVersion) {
    datasetVersion = next
    return
  }
  if (datasetVersion === next) return
  datasetVersion = next
  invalidatePanchayatDatasetCache()
}

function normalizeNames(value: any): V2Names {
  const en = String(value?.en || value?.name || "").trim()
  const hi = String(value?.hi || en).trim()
  const pa = String(value?.pa || en).trim()
  return {
    en,
    hi,
    pa,
  }
}

function normalizeDistrictNodeNames(value: InstitutionNode): V2Names {
  const en = String(value?.name_english || value?.name?.en || value?.code || value?.id || "").trim()
  const hi = String(value?.name_hindi || value?.name?.hi || en).trim()
  const pa = String(value?.name?.pa || en).trim()
  return { en, hi, pa }
}

function normalizeBlockNodeNames(value: InstitutionNode): V2Names {
  const en = String(value?.name_english || value?.name?.en || value?.code || value?.id || "").trim()
  const hi = String(value?.name_hindi || value?.name?.hi || en).trim()
  const pa = String(value?.name?.pa || en).trim()
  return { en, hi, pa }
}

function normalizeGpNodeNames(value: InstitutionNode): V2Names {
  const en = String(value?.name_english || value?.name?.en || value?.code || value?.id || "").trim()
  const hi = String(value?.name_hindi || value?.name?.hi || en).trim()
  const pa = String(value?.name?.pa || en).trim()
  return { en, hi, pa }
}

function normalizeWardNames(raw: Record<string, any>, wardNumber: number): V2Names {
  return {
    en: String(raw?.en?.[`ward${wardNumber}`] || "").trim(),
    hi: String(raw?.hi?.[`ward${wardNumber}`] || "").trim(),
    pa: String(raw?.pa?.[`ward${wardNumber}`] || "").trim(),
  }
}

function normalizeDistrictKey(value: string) {
  return normalizeString(value)
}

function normalizeBlockKey(blockId: string, districtId?: string) {
  return `${normalizeString(districtId || "")}::${normalizeString(blockId)}`
}

function normalizeGpKey(gpId: string, districtId?: string, blockId?: string) {
  return `${normalizeString(districtId || "")}::${normalizeString(blockId || "")}::${normalizeString(gpId)}`
}

function matchesRecordNames(value: { id: string; names: V2Names }, target: string): boolean {
  const normalizedTarget = normalizeString(target)
  if (!normalizedTarget) return false
  if (normalizeString(value.id) === normalizedTarget) return true
  return Object.values(value.names || EMPTY_NAMES).some((name) => normalizeString(name) === normalizedTarget)
}

async function fetchInstitutionChildren(args: {
  type: InstitutionNodeType
  parentCode?: string | null
  districtCode?: string | null
}): Promise<InstitutionNode[]> {
  const payload = await invokeEdgeFunction<PanchayatChildrenPayload>(
    "panchayat-get-children",
    {
      type: args.type,
      parent_code: args.parentCode ?? null,
      district_code: args.districtCode ?? null,
    },
  )
  return Array.isArray(payload?.children) ? payload.children : []
}

async function fetchDistrictBootstrapData(): Promise<PanchayatDistrictRecord[]> {
  const res = await fetch("/data/v2/districts.json", { cache: "no-cache" })
  if (!res.ok) throw new Error(`Failed to load districts: ${res.status}`)
  const payload = (await res.json()) as DistrictBootstrapPayload
  const districts = Array.isArray(payload?.districts) ? payload.districts : []
  return districts
    .map((entry) => ({
      id: String(entry?.id || "").trim(),
      names: normalizeNames(entry?.names || {}),
    }))
    .filter((entry) => entry.id)
}

async function fetchInstitutionData(
  code: string,
  districtCode: string,
  blockCode: string,
  type: "current" | "previous" = "current",
): Promise<PanchayatDataPayload | null> {
  const normalizedCode = String(code || "").trim()
  if (!normalizedCode) return null
  try {
    return await invokeEdgeFunction<PanchayatDataPayload>(
      "panchayat-get-data",
      {
        code: normalizedCode,
        district_code: districtCode,
        block_code: blockCode,
        type,
      },
    )
  } catch (error: any) {
    if (error && typeof error === "object" && (error as any).status === 404) return null
    throw error
  }
}

async function loadDistrictListData(): Promise<PanchayatDistrictRecord[]> {
  await ensureDatasetVersion()
  const cached = readFreshCacheEntry(districtListCache)
  if (cached) return cached.value
  if (districtListInFlight) return districtListInFlight

  const request = (async () => {
    reportPerfDebug("C", "src/lib/panchayat-data.ts", "[DEBUG] District list fetch started", {
      endpoint: "/data/v2/districts.json",
      type: "district",
    })
    const records = await fetchDistrictBootstrapData()
    districtListCache = { value: records, expiresAt: Date.now() + DISTRICT_TTL_MS }
    reportPerfDebug("C", "src/lib/panchayat-data.ts", "[DEBUG] District list fetch completed", {
      endpoint: "/data/v2/districts.json",
      districtCount: records.length,
    })
    return records
  })()

  districtListInFlight = request
  try {
    return await request
  } finally {
    if (districtListInFlight === request) {
      districtListInFlight = null
    }
  }
}

async function resolveDistrictRecord(districtId: string): Promise<PanchayatDistrictRecord | null> {
  const districts = await loadDistrictListData()
  return districts.find((district) => matchesRecordNames(district, districtId)) || null
}

async function loadBlockListData(districtId: string): Promise<PanchayatBlockRecord[]> {
  await ensureDatasetVersion()
  const district = await resolveDistrictRecord(districtId)
  if (!district) return []
  const cacheKey = normalizeDistrictKey(district.id)
  const cached = readFreshCacheEntry(blockListCache.get(cacheKey))
  if (cached) return cached.value
  blockListCache.delete(cacheKey)
  const inFlight = blockListInFlightCache.get(cacheKey)
  if (inFlight) return inFlight

  const request = (async () => {
    const children = await fetchInstitutionChildren({ type: "block", parentCode: district.id })
    const blocks = children.map((entry) => ({
      id: String(entry?.id || entry?.code || "").trim(),
      names: normalizeBlockNodeNames(entry),
      districtId: String(entry?.district_id || district.id).trim() || district.id,
    })).filter((entry) => entry.id)
    blockListCache.set(cacheKey, { value: blocks, expiresAt: Date.now() + BLOCK_TTL_MS })
    for (const block of blocks) {
      loadedDistrictByBlockKey.set(normalizeBlockKey(block.id, district.id), district.id)
      loadedDistrictByBlockKey.set(normalizeBlockKey(block.names.en, district.id), district.id)
      loadedDistrictByBlockKey.set(normalizeBlockKey(block.names.hi, district.id), district.id)
      loadedDistrictByBlockKey.set(normalizeBlockKey(block.names.pa, district.id), district.id)
    }
    return blocks
  })()

  blockListInFlightCache.set(cacheKey, request)
  try {
    return await request
  } finally {
    if (blockListInFlightCache.get(cacheKey) === request) {
      blockListInFlightCache.delete(cacheKey)
    }
  }
}

async function resolveBlockContext(blockId: string, districtId?: string): Promise<{ district: PanchayatDistrictRecord; block: PanchayatBlockRecord } | null> {
  if (districtId) {
    const district = await resolveDistrictRecord(districtId)
    if (!district) return null
    const blocks = await loadBlockListData(district.id)
    const block = blocks.find((entry) => matchesRecordNames(entry, blockId)) || null
    if (!block) return null
    return { district, block }
  }

  const districts = await loadDistrictListData()
  for (const district of districts) {
    const blocksEntry = readFreshCacheEntry(blockListCache.get(normalizeDistrictKey(district.id)))
    if (!blocksEntry) continue
    const block = blocksEntry.value.find((entry) => matchesRecordNames(entry, blockId)) || null
    if (block) return { district, block }
  }
  return null
}

async function loadGpListData(blockId: string, districtId?: string): Promise<PanchayatGramPanchayatRecord[]> {
  await ensureDatasetVersion()
  const context = await resolveBlockContext(blockId, districtId)
  if (!context) return []
  const cacheKey = normalizeBlockKey(context.block.id, context.district.id)
  const cached = readFreshCacheEntry(gpListCache.get(cacheKey))
  if (cached) return cached.value
  gpListCache.delete(cacheKey)
  const inFlight = gpListInFlightCache.get(cacheKey)
  if (inFlight) return inFlight

  const request = (async () => {
    const children = await fetchInstitutionChildren({ type: "gp", parentCode: context.block.id, districtCode: context.district.id })
    const gps = children.map((entry) => ({
      id: String(entry?.id || entry?.code || "").trim(),
      names: normalizeGpNodeNames(entry),
      districtId: context.district.id,
      blockId: context.block.id,
    })).filter((entry) => entry.id)
    gpListCache.set(cacheKey, { value: gps, expiresAt: Date.now() + GP_TTL_MS })
    for (const gp of gps) {
      loadedDistrictByGpKey.set(normalizeGpKey(gp.id, context.district.id, context.block.id), context.district.id)
      loadedDistrictByGpKey.set(normalizeGpKey(gp.names.en, context.district.id, context.block.id), context.district.id)
      loadedDistrictByGpKey.set(normalizeGpKey(gp.names.hi, context.district.id, context.block.id), context.district.id)
      loadedDistrictByGpKey.set(normalizeGpKey(gp.names.pa, context.district.id, context.block.id), context.district.id)
      loadedBlockByGpKey.set(normalizeGpKey(gp.id, context.district.id, context.block.id), context.block.id)
      loadedBlockByGpKey.set(normalizeGpKey(gp.names.en, context.district.id, context.block.id), context.block.id)
      loadedBlockByGpKey.set(normalizeGpKey(gp.names.hi, context.district.id, context.block.id), context.block.id)
      loadedBlockByGpKey.set(normalizeGpKey(gp.names.pa, context.district.id, context.block.id), context.block.id)
    }
    return gps
  })()

  gpListInFlightCache.set(cacheKey, request)
  try {
    return await request
  } finally {
    if (gpListInFlightCache.get(cacheKey) === request) {
      gpListInFlightCache.delete(cacheKey)
    }
  }
}

async function resolveGpRecord(gpId: string, districtId?: string, blockId?: string): Promise<PanchayatGramPanchayatRecord | null> {
  if (blockId) {
    const gps = await loadGpListData(blockId, districtId).catch(() => [])
    return gps.find((entry) => matchesRecordNames(entry, gpId)) || null
  }

  for (const gps of gpListCache.values()) {
    if (Date.now() >= gps.expiresAt) continue
    const gp = gps.value.find((entry) => matchesRecordNames(entry, gpId)) || null
    if (gp) return gp
  }

  return null
}

function buildInstitutionRecord(payload: PanchayatDataPayload, resolved: {
  gpCode: string
  districtId?: string
  blockId?: string
}): PanchayatInstitutionRecord | null {
  const panchayat = payload?.panchayat && typeof payload.panchayat === "object" ? payload.panchayat : null
  const raw = panchayat?.data && typeof panchayat.data === "object"
    ? panchayat.data
    : payload?.data && typeof payload.data === "object"
      ? payload.data
      : null
  if (!raw) return null

  const names: V2Names = {
    en: String(panchayat?.gram_panchayat || raw?.en?.gram_panchayat || "").trim(),
    hi: String(raw?.hi?.gram_panchayat || "").trim(),
    pa: String(raw?.pa?.gram_panchayat || "").trim(),
  }
  const districtNames: V2Names = {
    en: String(panchayat?.district || raw?.en?.district || "").trim(),
    hi: String(raw?.hi?.district || "").trim(),
    pa: String(raw?.pa?.district || "").trim(),
  }
  const blockNames: V2Names = {
    en: String(panchayat?.block || raw?.en?.block || "").trim(),
    hi: String(raw?.hi?.block || "").trim(),
    pa: String(raw?.pa?.block || "").trim(),
  }
  const sarpanch: V2Names = {
    en: String(raw?.en?.sarpanch || "").trim(),
    hi: String(raw?.hi?.sarpanch || "").trim(),
    pa: String(raw?.pa?.sarpanch || "").trim(),
  }

  const wardNumbers = new Set<number>()
  for (const lang of ["en", "hi", "pa"] as const) {
    const current = raw?.[lang]
    for (const key of Object.keys(current || {})) {
      const match = /^ward(\d+)$/i.exec(key)
      if (!match) continue
      wardNumbers.add(Number(match[1]))
    }
  }

  const wards = Array.from(wardNumbers)
    .filter((wardNumber) => Number.isFinite(wardNumber) && wardNumber > 0)
    .sort((a, b) => a - b)
    .map((wardNumber) => ({
      number: wardNumber,
      names: normalizeWardNames(raw, wardNumber),
    }))
    .filter((ward) => Object.values(ward.names).some(Boolean))

  return {
    id: String(panchayat?.id || resolved.gpCode).trim() || resolved.gpCode,
    code: String(panchayat?.code || payload?.code || resolved.gpCode).trim() || resolved.gpCode,
    type: panchayat?.type === "previous" ? "previous" : "current",
    category: "gp",
    names,
    districtId: String(resolved.districtId || "").trim() || districtNames.en,
    blockId: String(resolved.blockId || "").trim() || blockNames.en,
    districtNames,
    blockNames,
    sarpanch,
    wards,
    latitude: typeof panchayat?.latitude === "number" ? panchayat.latitude : null,
    longitude: typeof panchayat?.longitude === "number" ? panchayat.longitude : null,
    members: panchayat?.members ?? null,
    rawData: raw,
  }
}

const defaultPanchayatDataProvider: PanchayatDataProvider = {
  async getDistricts() {
    return loadDistrictListData()
  },
  async getBlocks(districtId: string) {
    return loadBlockListData(districtId)
  },
  async getGramPanchayats(blockId: string, districtId?: string) {
    return loadGpListData(blockId, districtId)
  },
  async getInstitution(gpId: string, districtId?: string, blockId?: string, recordType?: "current" | "previous") {
    await ensureDatasetVersion()
    const normalizedGpId = String(gpId || "").trim()
    if (!normalizedGpId) return null
    const resolvedRecordType = recordType === "previous" ? "previous" : "current"

    const gpRecord = await resolveGpRecord(normalizedGpId, districtId, blockId)
    const effectiveGpId = gpRecord?.id || normalizedGpId
    const effectiveDistrictId = String(
      gpRecord?.districtId
      || districtId
      || loadedDistrictByGpKey.get(normalizeGpKey(effectiveGpId, districtId, blockId))
      || "",
    ).trim()
    const effectiveBlockId = String(
      gpRecord?.blockId
      || blockId
      || loadedBlockByGpKey.get(normalizeGpKey(effectiveGpId, districtId, blockId))
      || "",
    ).trim()

    const cacheKey = `${resolvedRecordType}::${normalizeGpKey(effectiveGpId, effectiveDistrictId, effectiveBlockId)}`
    const cached = readFreshCacheEntry(institutionCache.get(cacheKey))
    if (cached) return cached.value
    institutionCache.delete(cacheKey)
    const inFlight = institutionInFlightCache.get(cacheKey)
    if (inFlight) return inFlight

    const request = (async () => {
      if (!effectiveDistrictId || !effectiveBlockId) {
        institutionCache.set(cacheKey, { value: null, expiresAt: Date.now() + INSTITUTION_TTL_MS })
        return null
      }
      const payload = await fetchInstitutionData(effectiveGpId, effectiveDistrictId, effectiveBlockId, resolvedRecordType)
      if (!payload) {
        institutionCache.set(cacheKey, { value: null, expiresAt: Date.now() + INSTITUTION_TTL_MS })
        return null
      }
      const institution = buildInstitutionRecord(payload, {
        gpCode: effectiveGpId,
        districtId: effectiveDistrictId,
        blockId: effectiveBlockId,
      })
      institutionCache.set(cacheKey, { value: institution, expiresAt: Date.now() + INSTITUTION_TTL_MS })
      return institution
    })()

    institutionInFlightCache.set(cacheKey, request)
    try {
      return await request
    } finally {
      if (institutionInFlightCache.get(cacheKey) === request) {
        institutionInFlightCache.delete(cacheKey)
      }
    }
  },
}

export function getPanchayatDataProvider(): PanchayatDataProvider {
  return activePanchayatDataProvider || defaultPanchayatDataProvider
}

export function setPanchayatDataProvider(provider: PanchayatDataProvider | null | undefined): void {
  activePanchayatDataProvider = provider ?? null
}

export function invalidatePanchayatDatasetCache(): void {
  districtListCache = null
  districtListInFlight = null
  blockListCache.clear()
  blockListInFlightCache.clear()
  gpListCache.clear()
  gpListInFlightCache.clear()
  institutionCache.clear()
  institutionInFlightCache.clear()
  hierarchyInFlight = null
  loadedDistrictByBlockKey.clear()
  loadedDistrictByGpKey.clear()
  loadedBlockByGpKey.clear()
}

export async function getPanchayatHierarchy(): Promise<V2Hierarchy> {
  if (hierarchyInFlight) return hierarchyInFlight

  const request = (async () => {
    const provider = getPanchayatDataProvider()
    const districts = await provider.getDistricts()
    const fullDistricts = await Promise.all(districts.map(async (district) => {
      const blocks = await provider.getBlocks(district.id)
      const fullBlocks = await Promise.all(blocks.map(async (block) => {
        const gps = await provider.getGramPanchayats(block.id, district.id)
        return {
          id: block.id,
          names: normalizeNames(block.names),
          gps: gps.map((gp) => ({
            id: gp.id,
            names: normalizeNames(gp.names),
          })),
        }
      }))
      return {
        id: district.id,
        names: normalizeNames(district.names),
        blocks: fullBlocks,
      }
    }))
    return { districts: fullDistricts }
  })()

  hierarchyInFlight = request
  try {
    return await request
  } finally {
    if (hierarchyInFlight === request) {
      hierarchyInFlight = null
    }
  }
}

export async function fetchPanchayatDataset(): Promise<any> {
  return getPanchayatHierarchy()
}

export async function refreshPanchayatDataset(): Promise<any> {
  invalidatePanchayatDatasetCache()
  return getPanchayatHierarchy()
}

export function datasetToMapLang(dataset: any, lang: "en" | "hi" | "pa" = "en"): PanchayatDataMap {
  const map: PanchayatDataMap = {}
  for (const d of dataset?.districts || []) {
    const dName = d?.names?.[lang]
    if (!dName) continue
    map[dName] = map[dName] || {}
    for (const b of d?.blocks || []) {
      const bName = b?.names?.[lang]
      if (!bName) continue
      const gps = (b?.gps || []).map((g: any) => g?.names?.[lang]).filter((n: any) => !!n)
      map[dName][bName] = gps
    }
  }
  return map
}

export async function getPanchayatMap(lang: "en" | "hi" | "pa" = "en"): Promise<PanchayatDataMap> {
  const dataset = await getPanchayatHierarchy()
  const map = datasetToMapLang(dataset, lang)
  reportPerfDebug("E", "src/lib/panchayat-data.ts", "[DEBUG] Panchayat map built", {
    lang,
    districtCount: Object.keys(map).length,
  })
  return map
}

export async function refreshPanchayatMap(lang: "en" | "hi" | "pa" = "en"): Promise<PanchayatDataMap> {
  invalidatePanchayatDatasetCache()
  return getPanchayatMap(lang)
}

export async function verifyDatasetConnectivity(): Promise<{ jsonOk: boolean; csvOk: boolean; jsonStatus?: number; csvStatus?: number }> {
  try {
    await getPanchayatDataProvider().getDistricts()
    return {
      jsonOk: true,
      csvOk: true,
      jsonStatus: 200,
      csvStatus: 200,
    }
  } catch {
    return {
      jsonOk: false,
      csvOk: false,
      jsonStatus: 500,
      csvStatus: 500,
    }
  }
}
